import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

interface UserRow {
  id: string;
  name: string;
  role: string;
}
interface CardRow {
  id: string;
  nickname: string | null;
  bank: string | null;
  lastFour: string | null;
}
interface BankRow {
  id: string;
  nickname: string | null;
  bank: string | null;
  accountNumberMasked: string | null;
}

const DURATIONS = [
  { m: 30, label: '30 minutes' },
  { m: 60, label: '1 hour' },
  { m: 120, label: '2 hours' },
  { m: 240, label: '4 hours' },
  { m: 480, label: '8 hours' },
];

/**
 * Web session creator — an admin grants a teammate (admin / accounts /
 * ops) the OTPs for chosen cards & bank accounts for a set duration,
 * without hand-carrying the phone. Posts to the same POST /sessions
 * endpoint the mobile app uses — no routing or session logic changes.
 */
export function SessionsPage() {
  const qc = useQueryClient();
  const users = useQuery<UserRow[]>({ queryKey: ['users'], queryFn: () => api('/api/users') });
  const cards = useQuery<CardRow[]>({ queryKey: ['cards'], queryFn: () => api('/api/cards') });
  const banks = useQuery<BankRow[]>({ queryKey: ['bank-accounts'], queryFn: () => api('/api/bank-accounts') });

  const [assignee, setAssignee] = useState('');
  const [pickedCards, setPickedCards] = useState<Set<string>>(new Set());
  const [pickedBanks, setPickedBanks] = useState<Set<string>>(new Set());
  const [duration, setDuration] = useState(60);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  const instrumentCount = pickedCards.size + pickedBanks.size;
  const canCreate = !!assignee && instrumentCount > 0 && !busy;

  const create = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api('/api/sessions', {
        method: 'POST',
        body: {
          assigneeUserId: assignee,
          cardIds: [...pickedCards],
          bankAccountIds: [...pickedBanks],
          durationMinutes: duration,
        },
      });
      setMsg({ kind: 'ok', text: 'Session created — the OTPs will route to that teammate.' });
      setAssignee('');
      setPickedCards(new Set());
      setPickedBanks(new Set());
      void qc.invalidateQueries({ queryKey: ['active-sessions'] });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message || 'Could not create the session' });
    } finally {
      setBusy(false);
    }
  };

  const field =
    'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-zinc-400';
  const loading = users.isLoading || cards.isLoading || banks.isLoading;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Create session</h1>
        <p className="text-sm text-muted-foreground">
          Grant a teammate the OTPs for chosen cards and accounts for a set time — no need to
          hand-carry the phone. They receive every OTP for those instruments until the session
          ends.
        </p>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">New session</CardTitle>
            <CardDescription>Who gets the OTPs, for which instruments, for how long.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                Give the OTPs to
              </label>
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className={field}>
                <option value="">— pick a teammate —</option>
                {(users.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} · {u.role.toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                Cards
              </label>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-input p-2">
                {(cards.data ?? []).length === 0 ? (
                  <p className="px-1 py-2 text-xs text-muted-foreground">No cards.</p>
                ) : (
                  (cards.data ?? []).map((c) => (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-zinc-50"
                    >
                      <input
                        type="checkbox"
                        checked={pickedCards.has(c.id)}
                        onChange={() => toggle(pickedCards, setPickedCards, c.id)}
                        className="h-4 w-4 accent-blue-600"
                      />
                      <span className="truncate">
                        {c.nickname ?? [c.bank, c.lastFour].filter(Boolean).join(' ')}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                Bank accounts
              </label>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-input p-2">
                {(banks.data ?? []).length === 0 ? (
                  <p className="px-1 py-2 text-xs text-muted-foreground">No bank accounts.</p>
                ) : (
                  (banks.data ?? []).map((b) => (
                    <label
                      key={b.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-zinc-50"
                    >
                      <input
                        type="checkbox"
                        checked={pickedBanks.has(b.id)}
                        onChange={() => toggle(pickedBanks, setPickedBanks, b.id)}
                        className="h-4 w-4 accent-blue-600"
                      />
                      <span className="truncate">
                        {b.nickname ?? b.bank ?? 'Account'}
                        {b.accountNumberMasked ? ` · ${b.accountNumberMasked}` : ''}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                Duration
              </label>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className={field}
              >
                {DURATIONS.map((d) => (
                  <option key={d.m} value={d.m}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>

            {msg ? (
              <p
                className={`text-sm ${msg.kind === 'ok' ? 'text-emerald-700' : 'text-rose-700'}`}
              >
                {msg.text}
              </p>
            ) : null}

            <button
              type="button"
              onClick={() => void create()}
              disabled={!canCreate}
              className="w-full rounded-lg bg-zinc-900 py-2.5 text-sm font-semibold text-white hover:bg-zinc-800 disabled:opacity-50"
            >
              {busy
                ? 'Creating…'
                : instrumentCount > 0
                  ? `Create session (${instrumentCount} instrument${instrumentCount === 1 ? '' : 's'})`
                  : 'Create session'}
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
